/**
 * Growth — Coupon performance.
 * Sources `/api/bi/growth/coupons/` and code-level detail endpoints.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import { ArrowLeft, Ticket } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { ErrorState } from '@/components/ui/ErrorState';
import { DataView } from '@/components/data/DataView';
import { KPICard } from '@/components/charts/KPICard';
import {
  useCouponDetail,
  useCouponPerformance,
  type CouponDetailCustomerRow,
  type CouponRow,
  type GrowthOrderDrillRow,
  type GrowthStoreDrillRow,
} from '@/api/bi';
import { rangeISO, useFilters } from '@/state/filters';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/cn';

type AttributionMode = 'first_touch' | 'last_touch' | 'touched';

const ATTRIBUTION_LABEL: Record<AttributionMode, string> = {
  first_touch: 'First-touch',
  last_touch: 'Last-touch',
  touched: 'Touched-code',
};

export function CouponsPage() {
  return (
    <>
      <PageHeader
        eyebrow="Growth"
        title="Coupons"
        description="Coupon redemptions, incentive CAC, CLV attribution, and code-level drill-downs."
        actions={
          <Button asChild variant="outline" size="sm">
            <Link to="/growth">
              <ArrowLeft className="h-3.5 w-3.5" /> Back
            </Link>
          </Button>
        }
      />
      <CouponsAnalyticsBody />
    </>
  );
}

export function CouponsAnalyticsBody({
  showKpis = true,
  detailReturnTo = '/growth/coupons',
}: {
  showKpis?: boolean;
  detailReturnTo?: string;
}) {
  const range = useFilters((s) => s.range);
  const storeIds = useFilters((s) => s.storeIds);
  const timeRangeMode = useFilters((s) => s.timeRangeMode);
  const { start: startISO, end: endISO } = useMemo(
    () => rangeISO(range, timeRangeMode),
    [range, timeRangeMode],
  );
  const filters = { start: startISO, end: endISO, storeIds };

  const query = useCouponPerformance({ ...filters, limit: 50 });
  const [search, setSearch] = useState('');
  const [mode, setMode] = useState<AttributionMode>('touched');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const detail = useCouponDetail(selectedId, filters);

  const data = query.data;
  const totals = data?.totals;
  const baseline = data?.baseline;
  const activeRollup = data?.attribution?.[mode];
  const selectedCoupon = data?.coupons.find((c) => c.coupon_id === selectedId) ?? null;

  const redemptionsTrend = useMemo(
    () => (data?.daily ?? []).map((d) => ({ value: d.redemptions })),
    [data?.daily],
  );
  const discountTrend = useMemo(
    () => (data?.daily ?? []).map((d) => ({ value: d.discount_amount })),
    [data?.daily],
  );

  const filteredCoupons = useMemo(() => {
    const rows = data?.coupons ?? [];
    if (!search.trim()) return rows;
    const q = search.trim().toLowerCase();
    return rows.filter(
      (r) => r.code.toLowerCase().includes(q) || (r.title ?? '').toLowerCase().includes(q),
    );
  }, [data?.coupons, search]);

  const columns = useMemo<ColumnDef<CouponRow, unknown>[]>(
    () => [
      {
        id: 'code',
        header: 'Code',
        cell: ({ row }) => (
          <div className="min-w-0">
            <div className="font-mono text-xs font-semibold uppercase tracking-wider text-foreground">
              {row.original.code}
            </div>
            {row.original.title ? (
              <div className="truncate text-[11px] text-muted-foreground">{row.original.title}</div>
            ) : null}
          </div>
        ),
      },
      {
        id: 'redemptions',
        header: () => <div className="text-right">Redemptions</div>,
        cell: ({ row }) => <Num value={row.original.redemptions} />,
      },
      {
        id: 'revenue',
        header: () => <div className="text-right">Revenue</div>,
        cell: ({ row }) => <Money value={row.original.gross_revenue} />,
      },
      {
        id: 'discount',
        header: () => <div className="text-right">Discount</div>,
        cell: ({ row }) => <Money value={row.original.discount_amount} tone="warning" />,
      },
      {
        id: 'clv',
        header: () => <div className="text-right">{ATTRIBUTION_LABEL[mode]} CLV</div>,
        cell: ({ row }) => <Money value={row.original.attribution[mode].revenue} />,
      },
      {
        id: 'cac',
        header: () => <div className="text-right">Incentive CAC</div>,
        cell: ({ row }) => <Money value={row.original.incentive_cac} />,
      },
      {
        id: 'payback',
        header: () => <div className="text-right">Payback</div>,
        cell: ({ row }) => (
          <div className="text-right">
            {row.original.payback == null ? '—' : `${row.original.payback.toFixed(2)}x`}
          </div>
        ),
      },
      {
        id: 'lift',
        header: () => <div className="text-right">AOV vs base</div>,
        cell: ({ row }) => {
          const v = row.original.aov_lift_pct;
          if (v == null) return <div className="text-right text-xs text-muted-foreground">—</div>;
          const tone = v > 0.02 ? 'success' : v < -0.02 ? 'danger' : 'neutral';
          return (
            <div className="text-right">
              <Badge tone={tone}>{fmt.delta(v, { unit: 'pct' })}</Badge>
            </div>
          );
        },
      },
    ],
    [mode],
  );

  return (
    <>
      {showKpis ? (
        <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
          <KPICard
            label="Redemptions"
            value={fmt.number(totals?.redemptions ?? 0)}
            loading={query.isLoading}
            trend={redemptionsTrend}
          />
          <KPICard label="Coupon revenue" value={fmt.npr(totals?.gross_revenue ?? 0)} loading={query.isLoading} />
          <KPICard
            label="Discount cost"
            value={fmt.npr(totals?.discount_amount ?? 0)}
            goodWhenUp={false}
            loading={query.isLoading}
            hint={`Discount share: ${fmt.pct(totals?.discount_share ?? 0)}`}
            trend={discountTrend}
          />
          <KPICard
            label="Incentive CAC"
            value={fmt.npr(totals?.incentive_cac ?? null)}
            goodWhenUp={false}
            loading={query.isLoading}
            hint="Discount cost divided by first-touch coupon customers."
          />
          <KPICard
            label="Avg CLV"
            value={fmt.npr(activeRollup?.avg_clv ?? totals?.avg_clv ?? 0)}
            loading={query.isLoading}
            hint={`${ATTRIBUTION_LABEL[mode]} selected-window CLV.`}
          />
        </div>
      ) : null}

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <Card>
          <CardBody>
            <div className="flex items-center justify-between">
              <h3 className="font-display text-base font-semibold">Active coupons</h3>
              <Badge tone="info" className="gap-1">
                <Ticket className="h-3 w-3" /> {fmt.number(totals?.active_coupons ?? 0)}
              </Badge>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3 text-center">
              <Stat label="Codes used" value={fmt.number(totals?.unique_coupons ?? 0)} />
              <Stat label="Redemption rate" value={fmt.pct(baseline?.redemption_rate ?? 0)} />
            </div>
          </CardBody>
        </Card>

        <Card className="lg:col-span-2">
          <CardBody>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h3 className="font-display text-base font-semibold">Attribution view</h3>
                <p className="text-xs text-muted-foreground">
                  Choose how customer value is credited when a customer touches multiple codes.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {(Object.keys(ATTRIBUTION_LABEL) as AttributionMode[]).map((key) => (
                  <Button
                    key={key}
                    variant={mode === key ? 'primary' : 'outline'}
                    size="sm"
                    onClick={() => setMode(key)}
                  >
                    {ATTRIBUTION_LABEL[key]}
                  </Button>
                ))}
              </div>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
              <Stat label="Customers" value={fmt.number(activeRollup?.customers ?? 0)} />
              <Stat label="Converted" value={fmt.number(activeRollup?.converted_customers ?? 0)} />
              <Stat label="Revenue / CLV" value={fmt.npr(activeRollup?.revenue ?? 0)} />
              <Stat label="Repeat rate" value={fmt.pct(activeRollup?.repeat_rate ?? 0)} />
            </div>
          </CardBody>
        </Card>
      </div>

      <div className="mt-4">
        {query.isError ? (
          <ErrorState
            description="Couldn't load coupon performance."
            onRetry={() => void query.refetch()}
          />
        ) : (
          <DataView<CouponRow>
            title="Coupon codes"
            description="Click a row to drill into customers, orders, and stores."
            data={filteredCoupons}
            columns={columns}
            isLoading={query.isLoading}
            onRowClick={(row) => setSelectedId(row.coupon_id)}
            search={{ value: search, onChange: setSearch, placeholder: 'Search code or title…' }}
            emptyTitle="No redemptions in this window"
          />
        )}
      </div>

      <CouponDetailSection
        selectedCoupon={selectedCoupon}
        detail={detail.data}
        detailReturnTo={detailReturnTo}
        isLoading={detail.isLoading}
        isError={detail.isError}
        onRetry={() => void detail.refetch()}
        onClose={() => setSelectedId(null)}
      />
    </>
  );
}

function CouponDetailSection({
  selectedCoupon,
  detail,
  detailReturnTo,
  isLoading,
  isError,
  onRetry,
  onClose,
}: {
  selectedCoupon: CouponRow | null;
  detail: ReturnType<typeof useCouponDetail>['data'];
  detailReturnTo: string;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onClose: () => void;
}) {
  const pageSize = 20;
  const [customerPage, setCustomerPage] = useState(1);
  const [orderPage, setOrderPage] = useState(1);
  const [storePage, setStorePage] = useState(1);
  useEffect(() => {
    setCustomerPage(1);
    setOrderPage(1);
    setStorePage(1);
  }, [selectedCoupon?.coupon_id]);

  const customerColumns = useMemo<ColumnDef<CouponDetailCustomerRow, unknown>[]>(
    () => [
      {
        id: 'customer',
        accessorFn: (row) => row.name || row.phone || `#${row.user_id}`,
        header: 'Customer',
        cell: ({ row }) => (
          <div>
            <DrillLink to={`/customers/${row.original.user_id}`} from={detailReturnTo}>
              #{row.original.user_id}
            </DrillLink>
            <div className="text-[11px] text-muted-foreground">
              {row.original.name || row.original.phone || 'Customer'}
            </div>
          </div>
        ),
      },
      { accessorKey: 'redemptions', header: () => <div className="text-right">Redemptions</div>, cell: ({ row }) => <Num value={row.original.redemptions} /> },
      { accessorKey: 'orders', header: () => <div className="text-right">Orders</div>, cell: ({ row }) => <Num value={row.original.orders} /> },
      { accessorKey: 'clv', header: () => <div className="text-right">CLV</div>, cell: ({ row }) => <Money value={row.original.clv} /> },
      { accessorKey: 'last_order', header: 'Last order', cell: ({ row }) => fmt.datetime(row.original.last_order) },
    ],
    [detailReturnTo],
  );
  const orderColumns = useMemo<ColumnDef<GrowthOrderDrillRow, unknown>[]>(
    () => [
      {
        accessorKey: 'order_id',
        header: 'Order',
        cell: ({ row }) => (
          <DrillLink to={`/orders/${row.original.order_id}`} from={detailReturnTo}>
            #{row.original.order_id}
          </DrillLink>
        ),
      },
      {
        id: 'customer',
        header: 'Customer',
        cell: ({ row }) =>
          row.original.customer_id == null ? (
            '—'
          ) : (
            <div>
              <DrillLink to={`/customers/${row.original.customer_id}`} from={detailReturnTo}>
                #{row.original.customer_id}
              </DrillLink>
              {row.original.customer_name ? (
                <div className="text-[11px] text-muted-foreground">{row.original.customer_name}</div>
              ) : null}
            </div>
          ),
      },
      { accessorKey: 'store_name', header: 'Store', cell: ({ row }) => row.original.store_name ?? '—' },
      { accessorKey: 'final_total', header: () => <div className="text-right">Final total</div>, cell: ({ row }) => <Money value={row.original.final_total} /> },
      { accessorKey: 'coupon_discount', header: () => <div className="text-right">Discount</div>, cell: ({ row }) => <Money value={row.original.coupon_discount ?? 0} tone="warning" /> },
      { accessorKey: 'completed_timestamp', header: 'Completed', cell: ({ row }) => fmt.datetime(row.original.completed_timestamp) },
    ],
    [detailReturnTo],
  );
  const storeColumns = useMemo<ColumnDef<GrowthStoreDrillRow, unknown>[]>(
    () => [
      { accessorKey: 'store_name', header: 'Store', cell: ({ row }) => row.original.store_name ?? '—' },
      { accessorKey: 'orders', header: () => <div className="text-right">Orders</div>, cell: ({ row }) => <Num value={row.original.orders} /> },
      { accessorKey: 'revenue', header: () => <div className="text-right">Revenue</div>, cell: ({ row }) => <Money value={row.original.revenue} /> },
      { accessorKey: 'avg_aov', header: () => <div className="text-right">AOV</div>, cell: ({ row }) => <Money value={row.original.avg_aov} /> },
    ],
    [],
  );

  if (!selectedCoupon) return null;
  return (
    <Card className="mt-4">
      <CardHeader>
        <div>
          <CardTitle>Drill-down</CardTitle>
          <div className="mt-1 flex items-center gap-2">
            <Badge tone="primary" className="font-mono uppercase">{selectedCoupon.code}</Badge>
            <span className="text-xs text-muted-foreground">
              {detail?.totals.customers ?? selectedCoupon.touched_customers} customers
            </span>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose}>Close</Button>
      </CardHeader>
      <CardBody>
        {isError ? (
          <ErrorState description="Couldn't load coupon drill-down." onRetry={onRetry} />
        ) : (
          <div className="grid gap-4">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Stat label="Orders" value={fmt.number(detail?.totals.orders ?? 0)} />
              <Stat label="Revenue" value={fmt.npr(detail?.totals.revenue ?? 0)} />
              <Stat label="Discount" value={fmt.npr(detail?.totals.discount_amount ?? 0)} />
              <Stat label="Redemptions" value={fmt.number(detail?.totals.redemptions ?? 0)} />
            </div>
            <div className="grid gap-4 xl:grid-cols-3">
              <DataView
                title="Customers"
                data={detail?.customers ?? []}
                columns={customerColumns}
                isLoading={isLoading}
                clientPagination
                pagination={{
                  page: customerPage,
                  pageSize,
                  total: detail?.customers.length ?? 0,
                  onPageChange: setCustomerPage,
                }}
                density="compact"
                emptyTitle="No customers"
              />
              <DataView
                title="Orders"
                data={detail?.orders ?? []}
                columns={orderColumns}
                isLoading={isLoading}
                clientPagination
                pagination={{
                  page: orderPage,
                  pageSize,
                  total: detail?.orders.length ?? 0,
                  onPageChange: setOrderPage,
                }}
                density="compact"
                emptyTitle="No orders"
              />
              <DataView
                title="Stores"
                data={detail?.stores ?? []}
                columns={storeColumns}
                isLoading={isLoading}
                clientPagination
                pagination={{
                  page: storePage,
                  pageSize,
                  total: detail?.stores.length ?? 0,
                  onPageChange: setStorePage,
                }}
                density="compact"
                emptyTitle="No stores"
              />
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border bg-surface px-3 py-2">
      <div className="kpi-num text-lg font-semibold">{value}</div>
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
    </div>
  );
}

function Num({ value }: { value: number | null | undefined }) {
  return <div className="kpi-num text-right text-sm">{fmt.number(value ?? 0)}</div>;
}

function Money({ value, tone }: { value: number | null | undefined; tone?: 'warning' }) {
  return (
    <div className={cn('kpi-num text-right text-sm', tone === 'warning' && 'text-warning')}>
      {value == null ? '—' : fmt.npr(value)}
    </div>
  );
}

function DrillLink({ to, from, children }: { to: string; from: string; children: ReactNode }) {
  return (
    <Link
      to={to}
      state={{ from }}
      className="font-mono text-xs font-semibold text-primary hover:underline"
    >
      {children}
    </Link>
  );
}
